/**
 * «Вхід»: email, password, «Забули пароль?». After the session ended —
 * «Сесія завершилась», after a password reset — «Пароль змінено»; signed in
 * → «Об'єкти» (the administrator's list for the administrator).
 */
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { type LoginResponse, emailSchema } from 'shared';

import { homePath } from '../../components/AppShell.js';
import { AuthLayout } from '../../components/AuthLayout.js';
import type { LoginState } from '../../components/RequireAuth.js';
import { errorText } from '../../components/errors.js';
import { useProfile } from '../../hooks/use-profile.js';
import { useLocale } from '../../i18n/locale-context.js';
import { ApiError, api } from '../../lib/api-client.js';
import { queryClient } from '../../lib/query-client.js';

export function LoginPage() {
  const { t, tf } = useLocale();
  const navigate = useNavigate();
  const state = (useLocation().state ?? {}) as LoginState;
  const profile = useProfile();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [checked, setChecked] = useState(false);

  const login = useMutation({
    mutationFn: () => api.post<LoginResponse>('/auth/login', { email: email.trim(), password }),
    onSuccess: (data) => {
      queryClient.clear();
      queryClient.setQueryData(['profile'], data.user);
      navigate(homePath(data.user.role), { replace: true });
    },
  });

  if (profile.data && !login.isPending) return <Navigate to={homePath(profile.data.role)} replace />;

  const emailOk = emailSchema.safeParse(email).success;
  const passwordOk = password.length >= 8;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setChecked(true);
    if (emailOk && passwordOk) login.mutate();
  };
  const formError =
    login.error instanceof ApiError && login.error.status === 401
      ? t('auth.login.invalid')
      : login.error
        ? errorText(login.error, tf)
        : null;

  return (
    <AuthLayout>
      <form className="auth-form" noValidate onSubmit={submit}>
        <h1>{t('auth.login.title')}</h1>
        {state.expired && !login.error ? <p className="note">{t('auth.login.expired')}</p> : null}
        {state.reset && !login.error ? <p className="note">{t('auth.login.reset_ok')}</p> : null}
        <label className="field">
          <span>{t('auth.email')}</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={checked && !emailOk}
          />
          {checked && !emailOk ? <small className="err">{t('auth.err.email')}</small> : null}
        </label>
        <label className="field">
          <span>{t('auth.password')}</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={checked && !passwordOk}
          />
          {checked && !passwordOk ? <small className="err">{t('auth.err.password')}</small> : null}
        </label>
        {formError ? (
          <p className="err" role="alert">
            {formError}
          </p>
        ) : null}
        <button className="btn primary wide" type="submit" disabled={login.isPending}>
          {t('auth.login.submit')}
        </button>
        <div className="auth-links">
          <Link to="/forgot-password">{t('auth.login.forgot')}</Link>
          <span>
            {t('auth.login.no_account')} <Link to="/signup">{t('auth.login.go_signup')}</Link>
          </span>
        </div>
      </form>
    </AuthLayout>
  );
}
